import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { branchesAPI } from '../services/api';
import { useAuth } from './AuthContext';

const BranchContext = createContext(null);

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

export const BranchProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load branches when authenticated
  const loadBranches = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const response = await branchesAPI.getAll({ active: true, limit: 100 });
      // paginatedResponse returns: { data: { items, total, page, limit } }
      const data = response.data?.data?.items || response.data?.items || response.data?.data || [];
      setBranches(Array.isArray(data) ? data : []);
      
      // Set default branch
      const savedBranchId = localStorage.getItem('selectedBranchId');
      
      if (savedBranchId && data.find(b => b._id === savedBranchId)) {
        // Use saved branch if still valid
        setSelectedBranch(data.find(b => b._id === savedBranchId));
      } else if (user?.branch_id) {
        // Use user's assigned branch
        const userBranch = data.find(b => b._id === user.branch_id || b._id === user.branch_id?._id);
        if (userBranch) {
          setSelectedBranch(userBranch);
          localStorage.setItem('selectedBranchId', userBranch._id);
        }
      } else if (data.length > 0) {
        // Use first branch as default
        setSelectedBranch(data[0]);
        localStorage.setItem('selectedBranchId', data[0]._id);
      }
    } catch (error) {
      console.error('Error loading branches:', error);
    }
    setLoading(false);
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  // Switch to a different branch
  const switchBranch = useCallback((branch) => {
    if (branch && branch._id) {
      setSelectedBranch(branch);
      localStorage.setItem('selectedBranchId', branch._id);
    }
  }, []);

  // Clear branch on logout
  const clearBranch = useCallback(() => {
    setSelectedBranch(null);
    setBranches([]);
    localStorage.removeItem('selectedBranchId');
  }, []);

  // Refresh branches list
  const refreshBranches = useCallback(async () => {
    await loadBranches();
  }, [loadBranches]);

  // Check if user can switch branches (owners can switch, others stay in their branch)
  const canSwitchBranch = user?.role === 'owner' || user?.role === 'lab_supervisor';

  // Get branch ID for API calls
  const getBranchId = useCallback(() => {
    return selectedBranch?._id || null;
  }, [selectedBranch]);

  const value = {
    branches,
    selectedBranch,
    loading,
    switchBranch,
    clearBranch,
    refreshBranches,
    canSwitchBranch,
    getBranchId,
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};

export default BranchContext;

